<script>
    import { onMount } from "svelte";
    import * as d3 from "d3";

    export let data = [];
    export let showAdminBands = false;
    export let administrations = [];
    export let wrapperClass = "";

    const margin = { top: 20, right: 15, bottom: 30, left: 30 };

    // SVG vs chart dimensions
    let svgWidth = 800;
    let svgHeight = 400;
    let wrapperEl;
    let chartWidth = svgWidth - margin.left - margin.right;
    let chartHeight = svgHeight - margin.top - margin.bottom;

    let svgEl, tooltipEl;
    let pointsGroup;

    let lastWidth = 0;
    const MIN_WIDTH_CHANGE = 10;

    // Data is already grouped by month with { month: "YYYY-MM", count: N }
    // Use d3.timeParse to create local dates (avoids UTC timezone issues)
    const parseMonth = d3.timeParse("%Y-%m");
    const parseDate = d3.timeParse("%Y-%m-%d");

    $: points = data.map((d) => ({
        date: parseMonth(d.month),
        count: +d.count,
    }));
    $: dates = points.map((d) => d.date);

    $: if (administrations.length && dates.length) {
        administrations.forEach((adm) => {
            adm.start = parseDate(adm.start) || new Date(adm.start);
            adm.end = adm.end
                ? parseDate(adm.end) || new Date(adm.end)
                : dates[dates.length - 1];
        });
    }

    let x, y, xAxisGroup, yAxisGroup, linePath, annotationGroup;

    function getWidth() {
        return wrapperEl.node().getBoundingClientRect().width;
    }

    function updateWidth() {
        const newWidth = getWidth();
        // if (Math.abs(newWidth - lastWidth) < MIN_WIDTH_CHANGE) return;
        lastWidth = newWidth;

        svgWidth = newWidth; // full SVG width
        svgHeight = Math.max(newWidth * 0.2, 250); // full SVG height
        chartWidth = svgWidth - margin.left - margin.right;
        chartHeight = svgHeight - margin.top - margin.bottom;

        if (svgEl && data.length) updateChart();
    }

    function drawAdministrationBands(svg, x, height) {
        if (!showAdminBands) return;
        const bandGroup = svg.select(".admin-bands");
        const bandHeight = chartHeight;

        // Draw subtle background rectangles
        const bands = bandGroup
            .selectAll("rect.admin-bg")
            .data(administrations);
        bands
            .join(
                (enter) => enter.append("rect").attr("class", "admin-bg"),
                (update) => update,
                (exit) => exit.remove(),
            )
            .attr("x", (d) => x(d.start))
            .attr("y", margin.top)
            .attr("width", (d) => x(d.end) - x(d.start))
            .attr("height", bandHeight)
            .attr("fill", (d) =>
                d.dem ? "rgba(103,142,230,1)" : "rgba(218,53,43,1)",
            )
            .attr("opacity", 0.06);

        // Draw vertical separator lines at boundaries (except first)
        const separators = bandGroup
            .selectAll("line.admin-separator")
            .data(administrations.filter((d, i) => i > 0));
        separators
            .join(
                (enter) =>
                    enter.append("line").attr("class", "admin-separator"),
                (update) => update,
                (exit) => exit.remove(),
            )
            .attr("x1", (d) => x(d.start))
            .attr("x2", (d) => x(d.start))
            .attr("y1", margin.top)
            .attr("y2", margin.top + bandHeight)
            .attr("stroke", "var(--border-color)")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "4,4")
            .attr("opacity", 0.4);
    }

    function styleAxis(g, isYAxis = false) {
        // Remove the axis line (domain)
        if (isYAxis) {
            // Remove tick lines on y-axis
            g.selectAll(".tick line").remove();
        } else {
            g.selectAll(".tick line")
                .attr("stroke", "var(--border-color)")
                .attr("stroke-width", 1);
        }
        g.select(".domain").remove();
        g.selectAll(".tick text")
            .style("font-size", "var(--scale-1)")
            .style("fill", "var(--gray-40)");
    }

    function createLine() {
        return d3
            .line()
            .x((d) => x(d.date))
            .y((d) => y(d.count))
            .curve(d3.curveMonotoneX);
    }

    function initChart() {
        if (!data.length) return;

        const svg = d3.select(svgEl);
        svg.selectAll("*").remove();

        svg.append("g").attr("class", "admin-bands");

        xAxisGroup = svg.append("g");
        yAxisGroup = svg.append("g");

        linePath = svg
            .append("path")
            .datum(points)
            .attr("fill", "none")
            .attr("stroke", "var(--text-color)")
            .attr("stroke-width", 2.5)
            .attr("stroke-linecap", "round")
            .attr("stroke-linejoin", "round");

        pointsGroup = svg.append("g").attr("class", "points");

        svg.append("g").attr("class", "admin-text");

        annotationGroup = svg.append("g").attr("class", "annotations");

        updateChart();
    }

    function updateChart() {
        if (!data.length) return;

        x = d3
            .scaleTime()
            .domain(d3.extent(points, (d) => d.date))
            .range([margin.left, margin.left + chartWidth]);
        y = d3
            .scaleLinear()
            .domain([0, d3.max(points, (d) => d.count)])
            .nice()
            .range([margin.top + chartHeight, margin.top]);

        xAxisGroup
            .attr("transform", `translate(0,${margin.top + chartHeight})`)
            .call(
                d3
                    .axisBottom(x)
                    .ticks(chartWidth < 600 ? 3 : 5)
                    .tickFormat(d3.timeFormat("%b %Y"))
                    .tickSizeOuter(0),
            )
            .call(styleAxis);

        const yTicks = y.ticks(3);
        yAxisGroup
            .attr("transform", `translate(${margin.left + 5},0)`)
            .call(
                d3
                    .axisLeft(y) // the inner ticks will be of size 3
                    .tickSizeOuter(0)
                    .ticks(3)
                    .tickFormat((d) => {
                        const thousands = Math.round(d / 1000);
                        return thousands > 0 ? `${thousands}K` : "";
                    }),
            )
            .call((g) => styleAxis(g, true));

        // Draw horizontal grid lines at y-axis tick positions
        const svg = d3.select(svgEl);

        drawAdministrationBands(d3.select(svgEl), x, chartHeight);
        linePath.attr("d", createLine());

        // Add annotation for the last data point
        const lastPoint = points[points.length - 1];
        const annotations = svg.select(".annotations");
        annotations.selectAll("*").remove();

        // Add dot at the end of the line
        annotations
            .append("circle")
            .attr("cx", x(lastPoint.date))
            .attr("cy", y(lastPoint.count))
            .attr("r", 4)
            .attr("fill", "var(--text-color)")
            .attr("stroke", "var(--background-color)")
            .attr("stroke-width", 1);

        const bandText = svg.select(".admin-text");
        const labels = bandText.selectAll("g.band-label").data(administrations);

        const labelGroups = labels
            .join(
                (enter) => {
                    const g = enter.append("g").attr("class", "band-label");

                    // background / shadow text
                    g.append("text").attr("class", "label-bg");

                    // foreground text
                    g.append("text").attr("class", "label-fg");

                    return g;
                },
                (update) => update,
                (exit) => exit.remove(),
            )
            .attr(
                "transform",
                (d) =>
                    `translate(${(x(d.start) + x(d.end)) / 2}, ${margin.top + chartHeight / 2})`,
            );

        labelGroups
            .select(".label-bg")
            .attr("text-anchor", "middle")
            .attr("class", "end-annotation end-annotation-month label-bg")
            .style("fill", "none")
            .style("stroke", "var(--background-color)")
            .style("stroke-width", 3)
            .style("stroke-linejoin", "round")
            .text((d) => d.label);

        labelGroups
            .select(".label-fg")
            .attr("text-anchor", "middle")
            .attr("class", "end-annotation end-annotation-month label-fg")
            .text((d) => d.label);

        // Add text annotation
        const annotation = annotations
            .append("g")
            .attr(
                "transform",
                `translate(${x(lastPoint.date)},${y(lastPoint.count)})`,
            );

        annotation
            .append("text")
            .attr("x", -10)
            .attr("y", 20)
            .attr("text-anchor", "end")
            .attr("class", "end-annotation end-annotation-month")
            .style("font-size", "var(--scale-2)")
            .text(d3.timeFormat("%B %Y")(lastPoint.date));

        annotation
            .append("text")
            .attr("x", -10)
            .attr("y", 0)
            .attr("text-anchor", "end")
            .attr("class", "end-annotation")
            .text(`${lastPoint.count.toLocaleString()} cases`);
    }

    onMount(() => {
        wrapperEl = d3.select(wrapperClass);
        initChart();
        updateWidth();
        let resizeTimeout;
        const listener = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateWidth, 50);
        };
        window.addEventListener("resize", listener);
        return () => window.removeEventListener("resize", listener);
    });

    $: svgEl && data.length && updateChart();
</script>

<svg bind:this={svgEl} width={svgWidth} height={svgHeight}></svg>
<div class="tooltip" bind:this={tooltipEl}></div>
